import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";

import type { ConversationMessage } from "../types.js";

/**
 * One turn rendered as a chat bubble. User messages right-aligned in
 * the brand colour; assistant messages left-aligned with a quiet
 * surface. Sources collapse into a `<details>` footer so a 15-source
 * answer doesn't dominate the transcript.
 */
export function MessageBubble({
  message,
  assistantName,
}: {
  message: ConversationMessage;
  assistantName: string | null;
}): JSX.Element {
  const isUser = message.role === "user";
  const speaker = isUser ? "Customer" : (assistantName ?? "Otto");

  return (
    <Box sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start" }}>
      <Box
        sx={[
          { maxWidth: "70%", borderRadius: 1, px: 2, py: 1.5, fontSize: 14 },
          isUser
            ? { bgcolor: "primary.main", color: "primary.contrastText" }
            : {
                bgcolor: "surfaceSoft",
                color: "text.primary",
                border: 1,
                borderColor: "divider",
              },
        ]}
      >
        <Box
          sx={[
            { fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" },
            isUser
              ? { color: (t) => alpha(t.palette.primary.contrastText, 0.7) }
              : { color: "text.disabled" },
          ]}
        >
          {speaker} · {new Date(message.created_at).toLocaleString()}
        </Box>

        {message.error_message ? (
          <Box sx={{ mt: 0.5, fontStyle: "italic", opacity: 0.9 }}>
            {speaker} failed to answer: {message.error_message}
          </Box>
        ) : (
          <Box sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>{message.content || "—"}</Box>
        )}

        {!isUser && message.sources.length > 0 && (
          <Box component="details" sx={{ mt: 1, fontSize: 12, opacity: 0.9 }}>
            <Box component="summary" sx={{ cursor: "pointer", userSelect: "none" }}>
              Sources ({message.sources.length})
            </Box>
            <Box component="ul" sx={{ mt: 0.5, display: "grid", gap: 0.5, listStyle: "none", m: 0, p: 0, pt: 0.5 }}>
              {message.sources.map((s, i) => (
                <Box component="li" key={i}>
                  {s.url ? (
                    <Box
                      component="a"
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ color: "inherit", textDecoration: "underline" }}
                    >
                      {s.title ?? s.url}
                    </Box>
                  ) : (
                    <span>
                      {s.title ?? `${s.source_type ?? "Source"} ${s.source_id ?? ""}`}
                    </span>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
